import {
  ELEMENT_CONTROLLER_ID,
  ELEMENT_CONTROLLER_ROOT_ATTRIBUTE,
} from './base-controllers/ElementController';
import { onReady, onLoad } from './utils/dom';

const PRE_INIT_CLASS = 'no-js-init';
const PRE_LOAD_CLASS = 'no-js-load';
const INIT_DONE_CLASS = 'js-init';
const LOAD_DONE_CLASS = 'js-load';

/**
 * Class for handling controllers lifecycle.
 *
 * Usage:
 *
 *     const initializer = new Initializer([options]);
 *     initializer.init('global', document, [MyController, OtherController]);
 *
 * Available options:
 *
 * - setClasses {boolean} Set classes on the html element on DOM ready and
 *   window load events, both after all controller callbacks have run. Defaults
 *   to true.
 */
export default class Initializer {
  /**
   * Set options.
   *
   * @param {object} options - Default option overrides.
   */
  constructor(options) {
    this._instances = {};
    this.opt = Object.assign(
      {},
      {
        setClasses: true,
      },
      options,
    );
  }

  /**
   * Initialize all specified controllers.
   *
   * @param {string} id - A unique id that represents this instance
   * @param {Element} el - The element to initialize controllers on
   * @param {Array.<Function>?} controllerClasses - Classes/constructors.
   */
  init(id, el, controllerClasses) {
    if (controllerClasses) {
      this.controllerClasses = controllerClasses;
    }

    onReady(this._init.bind(this, id, el));
    onLoad(this._load.bind(this, id, el));
  }

  /**
   * Destroy all controllers
   *
   * @param {string} id - A unique id that represents this instance
   * @returns {boolean}
   */
  destroy(id) {
    const instances = this._instances[id];
    if (!instances) {
      return true;
    }
    return instances.every((instance) => instance.destroy());
  }

  /**
   * Initialize controllers for each root element.
   *
   * If a controller's root element doesn't exist in the DOM, the controller
   * isn't initialized at all.
   *
   * @param {string} id - A unique id that represents this instance
   * @param {Element} el - The element to initialize controllers on
   */
  _init = (id, el) => {
    this._instances[id] = [];
    this.controllerClasses.forEach((Controller) => {
      // IMPORTANT! Don't use `name` for any logic since it will be mangled
      // to a single letter when minified.
      const { _baseId, id: _id, name } = Controller;

      if (process.env.NODE_ENV === 'development') {
        // Validate controller class name on dev
        if (!name) {
          throw new Error(
            `Controller class (baseId: '${_baseId}', id: '${_id}') is missing a name`,
          );
        }
      }

      if (!_baseId) {
        throw new Error(
          `Controller '${name}' is missing a static '_baseId' property, which should be set on its parent`,
        );
      }
      if (!_id) {
        throw new Error(
          `Controller '${name}' is missing a static 'id' property`,
        );
      }

      if (process.env.NODE_ENV === 'development') {
        // Validate controller ID on dev
        if (_id !== name) {
          throw new Error(
            `A controller's \`id\` should be the class name, got id '${_id}' for class '${name}'`,
          );
        }
      }

      // Default to one null item when initializing on Document to keep the
      // same forEach flow for controllers not bound to elements.
      let roots = el instanceof Document ? [null] : [];
      if (_baseId === ELEMENT_CONTROLLER_ID) {
        roots = Array.from(
          el.querySelectorAll(
            `[${ELEMENT_CONTROLLER_ROOT_ATTRIBUTE}~="${_id}"]`,
          ),
        );
        if (!roots.length) {
          // Block is stripped in production build
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              `No element found for controller '${_id}', skipping initialization. Set a root with attribute ${ELEMENT_CONTROLLER_ROOT_ATTRIBUTE}="${_id}" to use it.`,
            );
          }
          return;
        }
      }

      roots.forEach((element) => {
        const instance = new Controller();
        switch (_baseId) {
          case ELEMENT_CONTROLLER_ID:
            instance._init(Controller, element);
            break;

          default:
            instance._init(Controller);
            break;
        }
        this._instances[id].push(instance);
      });
    });

    if (this.opt.setClasses && el instanceof Document) {
      document.documentElement.classList.remove(PRE_INIT_CLASS);
      document.documentElement.classList.add(INIT_DONE_CLASS);
    }
  };

  // eslint-disable-next-line jsdoc/require-param
  /**
   * Call load handler on all instances.
   *
   * @see _init(id, el)
   */
  _load = (id, el) => {
    // Assumes _init always runs before or at the same time, never after.
    // According to the MDN docs on readyState, a state of 'complete' indicates
    // that the load event is ABOUT to fire, so this should always come after.
    // Browsers and bugs though...
    // A small timeout should hopefully add enough safety and possibly give
    // browsers some extra time to paint everything that loaded as well.
    // https://developer.mozilla.org/en-US/docs/Web/API/Document/readyState
    // TODO: think about this.
    setTimeout(() => {
      this._instances[id].forEach((instance) => {
        instance.load();
      });
      if (this.opt.setClasses && el instanceof Document) {
        document.documentElement.classList.remove(PRE_LOAD_CLASS);
        document.documentElement.classList.add(LOAD_DONE_CLASS);
      }
    }, 50);
  };

  /**
   * Get all controller instances.
   *
   * @readonly
   * @returns {Array.<object>}
   */
  get instances() {
    return this._instances;
  }
}
